import 'moment/min/locales';

import { tify } from 'chinese-conv';
import interpolate from 'color-interpolate';
import { i18n, TFunction } from 'i18next';
import { DateTime } from 'luxon';
import moment from 'moment-timezone';
import numbro from 'numbro';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import { uiActions } from '../actions';
import { uiConstants } from '../constants';
import { GraphColorStyleEnum } from '../enums';
import useDispatch from '../hooks/useDispatch';
import * as Types from '../shared/types';
import { Language } from '../shared/types';

const candleStickColorMap = (
  graphColorStyle: GraphColorStyleEnum
): { up: string; down: string } => {
  switch (graphColorStyle) {
    case GraphColorStyleEnum.greenUpRedDown: {
      return { up: '#1c9d01', down: '#ee1517' };
    }
    case GraphColorStyleEnum.greenUpBlackDown: {
      return { up: '#1c9d01', down: 'black' };
    }
    case GraphColorStyleEnum.redUpBlackDown: {
      return { up: '#ee1517', down: 'black' };
    }
    default: {
      return { up: '#1c9d01', down: '#ee1517' };
    }
  }
};

export default {
  formatMouseCoordinateMoney,
  formatMoney,
  formatDeltaMoney,
  formatDate,
  formatDateTime,
  formatDateLocal,
  formatDateTimeLocalFromTimestamp,
  formatTimeLocalFromTimestamp,
  formatDateLocalFromTimestamp,
  formatDateLocalForPicker,
  formatTimeLocalForPicker,
  formatDateTimeLocal,
  formatDeltaDateTime,
  formatTime,
  formatLargeMoney,
  formatDecimal,
  utcTodayDate,
  swapLocalToUTCDate,
  swapUTCToLocalDateTime,
  hkTimeToUTC,
  navigateCCASS,
  formatWeek,
  formatMonth,
  colorForChange,
  showError,
  loadHKTime,
  loadAsHKTime,
  formatPercentage,
  tradChify,
  candleStickColorMap,
  formatMinuteTime,
};

const HK_TZONE = 'Asia/Hong_Kong';

function formatMouseCoordinateMoney(value: number): string {
  // If price > 10000, no decimal place, else show 4 significant figures
  if (value === null) {
    return '-';
  } else if (value > 10_000) {
    return value.toFixed(0);
  } else {
    return value.toPrecision(4);
  }
}

function formatMoney(value: number): string {
  if (value === null) return '-';
  return numbro(value).format('$0,0.000');
}

function formatDeltaMoney(value: number): string {
  if (value === null) return '-';
  return numbro(value).format('+$0,0.000');
}

function formatDate(tickItem: string | Date): string {
  return moment(tickItem).utc().format(uiConstants.DATE_FORMAT);
}

function formatDateTime(tickItem: string | Date): string {
  return moment(tickItem).utc().format(uiConstants.DATETIME_FORMAT);
}

function formatDateTimeLocalFromTimestamp(tickItem: number): string {
  return moment.unix(tickItem).format(uiConstants.DATETIME_FORMAT);
}

function formatTimeLocalFromTimestamp(tickItem: number): string {
  return moment.unix(tickItem).format(uiConstants.TIME_FORMAT);
}

function formatDateLocal(tickItem: string | Date): string {
  return moment(tickItem).format(uiConstants.DATE_FORMAT);
}

function formatDateLocalFromTimestamp(tickItem: number): string {
  return moment.unix(tickItem).format(uiConstants.DATE_FORMAT);
}

function formatDateLocalForPicker(tickItem: string | Date): string {
  return moment(tickItem).format(uiConstants.DATE_FORMAT_FOR_PICKER);
}

function formatTimeLocalForPicker(tickItem: string | Date): string {
  return moment(tickItem).format(uiConstants.TIME_FORMAT);
}

function formatDateTimeLocal(tickItem: string | Date): string {
  return moment(tickItem).format(uiConstants.DATETIME_FORMAT);
}

function formatMinuteTime(tickItem: string | Date): string {
  return moment(tickItem).format(uiConstants.MINUTE_TIME_FORMAT);
}

function formatDeltaDateTime(
  i18n: i18n,
  tickItem: string | Date | moment.Moment
): string {
  const localeForLang = {
    en: 'en',
    zh: 'zh-hk',
  };
  return moment
    .utc(tickItem)
    .locale(localeForLang[i18n.language as Language])
    .fromNow();
}

function formatTime(tickItem: string | Date): string {
  return moment(tickItem).utc().format(uiConstants.TIME_FORMAT);
}

function formatWeek(i18n: i18n, date: string | Date): string {
  const week = moment.utc(date).week();
  return i18n.language === 'zh'
    ? `第${week}週`
    : `${numbro(week).format({ output: 'ordinal' })} Week`;
}

function formatDecimal(value: number): string {
  return numbro(value).format({ totalLength: 5 });
}

function formatMonth(i18n: i18n, date: string | Date): string {
  const month = moment.utc(date).month();
  const months = [
    '一',
    '二',
    '三',
    '四',
    '五',
    '六',
    '七',
    '八',
    '九',
    '十',
    '十一',
    '十二',
  ];
  return i18n.language === 'zh'
    ? `${months[month]}月`
    : moment.utc(date).format('MMM');
}

function formatLargeMoney(
  i18n: i18n,
  value: number,
  totalLength?: number
): string {
  if (value === undefined || value === null) {
    return i18n.language === 'zh' ? '沒有數據' : 'Not Available';
  }
  if (value < 0) {
    return '-' + formatLargeMoney(i18n, -value, totalLength);
  }
  let formatted = `${value}`;
  if (i18n.language === 'zh') {
    let unit = '';
    if (value >= 1_000_000_000_000) {
      unit = '萬億';
      formatted = `${value / 1_000_000_000_000}`;
    } else if (value >= 100_000_000) {
      unit = '億';
      formatted = `${value / 100_000_000}`;
    } else if (value >= 10_000) {
      unit = '萬';
      formatted = `${value / 10_000}`;
    }
    formatted = numbro(formatted)
      .format(
        totalLength === undefined ? '0.00' : { average: false, totalLength }
      )
      .replace(/(\.\d*[1-9])0+$|\.0*$/, '$1'); // remove trailing zeros

    return `${formatted}${unit}`;
  }
  return totalLength === undefined
    ? numbro(value)
        .format({
          average: true,
          thousandSeparated: true,
          mantissa: 2,
        })
        .toUpperCase()
    : numbro(value)
        .format({
          average: true,
          totalLength,
        })
        .toUpperCase();
}

function utcTodayDate(): Date {
  return swapLocalToUTCDate(new Date());
}

function swapLocalToUTCDate(date?: string | Date, region = 'hk'): Date {
  let tz = HK_TZONE;
  if (region === 'us') tz = 'America/New_York';
  return moment
    .utc(
      moment(date).tz(tz).format(uiConstants.DATE_FORMAT),
      uiConstants.DATE_FORMAT
    )
    .toDate();
}

function swapUTCToLocalDateTime(date: string | Date, region = 'hk'): Date {
  let tz = HK_TZONE;
  if (region === 'us') tz = 'America/New_York';
  return moment
    .tz(
      moment.utc(date).format(uiConstants.DATETIME_FORMAT),
      uiConstants.DATETIME_FORMAT,
      tz
    )
    .toDate();
}

function hkTimeToUTC(date: string | Date): moment.Moment {
  return moment.tz(date, HK_TZONE).utc();
}

function loadHKTime(date: string): moment.Moment {
  return moment.utc(date).subtract(8, 'hours');
}

function loadAsHKTime(date: string): moment.Moment {
  return moment.utc(date).add(8, 'hours');
}

function navigateCCASS(
  stockCode: string,
  when: string | Date,
  region: Types.Region
): void {
  const { dispatch, history } = this.props;

  dispatch(
    uiActions.ccassPage.dateChanged(
      moment(when, uiConstants.DATE_FORMAT).toDate()
    )
  );
  dispatch(uiActions.ccassPage.compareDateChanged());
  dispatch(uiActions.ccassPage.stockCodeChanged(stockCode));
  history.push(`/${region}/ccass`);
}

export const useNavigateCCASS = (): ((
  stockCode: string,
  when?: string | Date
) => void) => {
  const dispatch = useDispatch();
  const nagivate = useNavigate();
  return useCallback(
    (stockCode: string, when?: string | Date) => {
      const date = when
        ? moment(when, [
            uiConstants.DATE_FORMAT,
            uiConstants.DATE_FORMAT_AWS,
          ]).toDate()
        : new Date();
      dispatch(uiActions.ccassPage.dateChanged(date));
      dispatch(uiActions.ccassPage.compareDateChanged());
      dispatch(uiActions.ccassPage.stockCodeChanged(stockCode));
      nagivate(`/hk/ccass`);
    },
    [dispatch, nagivate]
  );
};

const positiveColormap = interpolate(['#006600', '#00cc00']);
const negativeColormap = interpolate(['#660000', '#cc0000']);

function colorForChange(change: number): string {
  if (Math.abs(change) < 0.001) return 'grey';
  return change >= 0
    ? positiveColormap(change * 5)
    : negativeColormap(-change * 5);
}

function showError(t: TFunction, error?: Error): string | null {
  if (error) {
    return t('something-wrong');
  }
  return null;
}

function formatPercentage(v: number | string): string {
  return (Number.parseFloat(String(v)) * 100).toFixed(2) + '%';
}

/**
 * Convert string to traditional Chinese
 */
function tradChify(str: string): string {
  if (!str) return '';
  return tify(str);
}

// bootstrap has tiny size but typescript misses the type
export const bsTiny = 'tiny' as 'sm';

export const correctedStrapiMarkdown = (markdown: string): string => {
  // use double backslash to replace the special character in RegExp
  return markdown.replaceAll(
    new RegExp('\\(.*/uploads/', 'g'),
    '(https://datalouder-cms.s3.ap-east-1.amazonaws.com/'
  );
};

/**
 * Validate whether a string is YYYYMMDD date string.
 * Checking the digits number should suffice.
 * https://app.shortcut.com/datalouder/story/3654/validate-date-before-quering-ccass
 */
export const validateDateStr = (dateStr: string): boolean => {
  return new RegExp('^\\d{8}$').test(dateStr);
};

export const dateToDateStr = (date: Date): string =>
  DateTime.fromJSDate(date).toFormat('yyyyMMdd');

export const capitalizeFirstLetter = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};
